import { getUserInitials } from '../../../../utils/Auth.util';
import { useAppSelector } from '../../../../hooks/redux';
import {
  InvitedUserDetails,
  RegisteredUserDetails,
  SearchUserDTO
} from '../../../../types/Team.types';
import { useDispatch } from 'react-redux';
import {
  setCurrentUser,
  setSorting
} from '../../../../store/slices/userManagementSlice';
import { useUserManagement } from '../../../hooks/useUserManagement';
import { formatDate } from '../../../../utils/Date.util';
import { TableHeader } from '../../../tables/AISystemsTable/components/TableHeader/TableHeader';
import { TruncatedText } from '../../../../utils/TruncatedText';

type UsersTableProps = Readonly<{
  searchResults: SearchUserDTO | null;
}>;

export function UsersTable({ searchResults }: UsersTableProps) {
  const dispatch = useDispatch();
  const { teamInvitations, teamMembers, onlyShowInvitedUsers, sorting } =
    useAppSelector((state) => state.userManagement);
  const account = useAppSelector((state) => state.accountDefault.account);
  const { fetchAiSystemsForUser } = useUserManagement();

  const handleUserClick = (
    currentUser: RegisteredUserDetails,
    isRegistered: boolean,
  ) => {
    dispatch(setCurrentUser({ ...currentUser, isRegistered }));
    if (!account) return;

    fetchAiSystemsForUser(account?.id, currentUser.userId);
  };

  const handleInvitedUserClick = (
    currentUser: InvitedUserDetails,
    isRegistered: boolean
  ) => {
    dispatch(setCurrentUser({ ...currentUser, isRegistered }));
    if (!account) return;

    fetchAiSystemsForUser(account?.id, currentUser.userId);
  };

  const displayTeamMembers = searchResults
    ? searchResults.registeredUsers.items
    : teamMembers?.registeredUsers;

  const displayTeamMembersCount = searchResults
    ? searchResults.registeredUsers.count
    : teamMembers?.total || 0;

  const displayInvitedUsers = searchResults
    ? searchResults.invitedUsers.items
    : teamInvitations?.invitedUsers;

  const displayInvitedUsersCount = searchResults
    ? searchResults.invitedUsers.count
    : teamInvitations?.total || 0;

  const handleSort = (sortKey: string) => {
    if (sorting.sortBy === sortKey) {
      const newOrder = sorting.sortOrder === 'asc' ? 'desc' : 'asc';
      dispatch(setSorting({ sortBy: sortKey, sortOrder: newOrder }));
    } else {
      dispatch(setSorting({ sortBy: sortKey, sortOrder: 'asc' }));
    }
  };

  const renderTeamMemberRow = ({
    key,
    initials,
    displayName,
    email,
    assignedAiSystems,
    lastActive,
    onClick,
    rowClassName = ''
  }: {
    key: string | number;
    initials: string;
    displayName: string;
    email: string;
    assignedAiSystems: number | string | undefined;
    lastActive: string;
    onClick: () => void;
    rowClassName?: string;
  }) => (
    <tr
      key={key}
      className={`w-full flex flex-row cursor-pointer bg-white border-t border-gray-100 ${rowClassName}`}
      onClick={onClick}
    >
      <td className="w-[33.333%] flex py-4 px-6 gap-1 text-sm font-medium items-center text-gray-900v2">
        <div className="flex flex-row gap-4 self-stretch justify-center items-center">
          <div className="flex pt-[6px] pr-[1px] pb-[6px] pl-[2px] rounded-[12px] w-6 h-6 bg-gray-100 justify-center items-center text-xs font-normal text-gray-800">
            {initials}
          </div>
          {displayName}
        </div>
      </td>
      <td className="w-[33.333%] min-w-0 flex py-4 px-6 gap-1 text-sm font-normal text-gray-550 items-center">
        <TruncatedText text={email} className="text-left" />
      </td>
      <td className="w-[16.667%] flex py-4 px-6 gap-1 text-sm font-normal text-gray-550 items-center">
        {assignedAiSystems || '--'}
      </td>
      <td className="w-[16.667%] flex py-4 px-6 gap-1 text-sm font-normal text-gray-550 items-center">
        {lastActive}
      </td>
    </tr>
  );

  return (
    <div className="w-full">
      <div className="w-full space-y-4 ">
        <div className="w-full overflow-hidden rounded-lg bg-white shadow-sm border border-solid border-gray-250v2">
          <table className="w-full border-collapse">
            <div className="flex sticky top-0 border-b border-gray-100">
              <TableHeader
                type="RegisteredUsers"
                invitedUserOnly={
                  onlyShowInvitedUsers || displayTeamMembersCount === 0
                }
                sortBy={sorting.sortBy}
                sortOrder={sorting.sortOrder}
                onSort={handleSort}
              />
            </div>
            <tbody>
              {!onlyShowInvitedUsers &&
                displayTeamMembers &&
                displayTeamMembersCount > 0 &&
                displayTeamMembers?.map(
                  (member: RegisteredUserDetails, index: number) =>
                    renderTeamMemberRow({
                      key: member.userId || index,
                      initials: getUserInitials(
                        member.firstName + ' ' + member.lastName
                      ),
                      displayName: member.firstName + ' ' + member.lastName,
                      email: member.email,
                      assignedAiSystems: member.assignedAiSystems,
                      lastActive: member.lastActive
                        ? formatDate(member.lastActive)
                        : '--',
                      onClick: () => handleUserClick(member, true),
                      rowClassName: 'hover:bg-gray-50'
                    })
                )}
            </tbody>
            {displayInvitedUsers && displayInvitedUsersCount > 0 && (
              <tbody>
                {!onlyShowInvitedUsers &&
                  teamMembers &&
                  teamMembers.total > 0 &&
                  displayTeamMembersCount > 0 && (
                    <tr className="w-full flex flex-row cursor-pointer bg-white border-t border-gray-100">
                      <td
                        colSpan={4}
                        className="w-full flex py-3 px-6 gap-1 bg-gray-40 tracking-wider text-xs font-medium uppercase text-gray-550 border-t border-gray-100"
                      >
                        Invited Users
                      </td>
                    </tr>
                  )}
                {displayInvitedUsers?.map((invitation, index) =>
                  renderTeamMemberRow({
                    key: invitation.userId || index,
                    initials: '-',
                    displayName: '--',
                    email: invitation.email,
                    assignedAiSystems: invitation.assignedAiSystems,
                    lastActive: '--',
                    onClick: () => handleInvitedUserClick(invitation, false)
                  })
                )}
              </tbody>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
